import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Database, get, ref, remove } from '@angular/fire/database';
import {
  Firestore,
  doc,
  setDoc,
  docData,
  DocumentReference,
  collection,
  getDocs,
  deleteDoc,
  addDoc,
} from '@angular/fire/firestore';
import { API_URL, ASSISTANTS_URL, AuthHttpHandleService } from '@contler/utils';
import { set } from 'firebase/database';
import { getDoc } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';

import {
  Currency,
  EstablishmentType,
  HotelConfigModel,
  HotelListItem,
  HotelModel,
  PaginatedHotels,
  RoomModel,
  SpecialZoneModel,
  ZoneModel,
} from '../models';
import { EcommerceModel } from '../models/ecommerce.model';
import { IconModel } from '../models/icon.model';
import { LinkWithPreviewResponse } from '../models/link-with-preview-response.model';
import { NewHotelRequest } from '../models/new-hotel-request.model';
import { ZoneEmail } from '../models/zone-email.model';

@Injectable({
  providedIn: 'root',
})
export class HotelService {
  private http: HttpClient;
  private db = inject(Firestore);
  private rtdb = inject(Database);

  private authHandle = inject(AuthHttpHandleService);
  private apiUrl = inject(API_URL);
  private assistantsUrl = inject(ASSISTANTS_URL, { optional: true }) as string;

  constructor() {
    this.http = new HttpClient(this.authHandle);
  }

  getIcons(): Promise<IconModel[]> {
    const path = 'icons';
    const iconsRef = ref(this.rtdb, path);
    return get(iconsRef)
      .then((snapshot) => snapshot.val())
      .then((icons) => Object.values(icons));
  }

  prepareHotelForAssistant(hotelId: string) {
    const url = new URL(`/chat/prepare-hotel-for-assistant`, this.assistantsUrl);
    return this.http.post(url.toString(), {
      hotelId,
    });
  }

  getSpecialZones() {
    const url = new URL('/special-zone', this.apiUrl);
    return this.http.get<SpecialZoneModel[]>(url.toString());
  }

  getRoomByNumber(hotelId: string, roomNumber: string) {
    const url = new URL(`/room/number/${roomNumber}`, this.apiUrl);
    url.searchParams.append('hotelId', hotelId);

    return this.http.get<RoomModel>(url.toString());
  }

  async getCurrencies(): Promise<Currency[]> {
    const collectionRef = collection(this.db, 'currencies');

    const snapshot = await getDocs(collectionRef);
    const currencies: Currency[] = [];
    snapshot.forEach((doc) => {
      currencies.push(doc.data() as Currency);
    });
    return currencies;
  }

  saveCurrency(currency: Currency) {
    const collectionRef = collection(this.db, 'currencies');
    return addDoc(collectionRef, currency);
  }

  async deleteHotel(hotelUid: string) {
    const languagesRef = ref(this.rtdb, `languages/${hotelUid}`);
    await remove(languagesRef);
    const configRef = doc(this.db, `hotelConfig/${hotelUid}`);
    await deleteDoc(configRef);
    const url = new URL(`/hotel/${hotelUid}`, this.apiUrl);
    const deletedHotelResponse = await firstValueFrom(this.http.delete(url.toString()));
    return deletedHotelResponse;
  }

  async getEstablishmentTypes(): Promise<EstablishmentType[]> {
    const collectionRef = collection(this.db, 'establishment-types');

    const snapshot = await getDocs(collectionRef);
    const establishmentTypes: EstablishmentType[] = [];
    snapshot.forEach((doc) => {
      const establishmentType = doc.data() as EstablishmentType;
      establishmentType.key = doc.id;
      establishmentTypes.push(establishmentType);
    });
    return establishmentTypes;
  }

  async getEstablishmentType(key: string): Promise<EstablishmentType | undefined> {
    const docRef = doc(this.db, `establishment-types/${key}`);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return undefined;
    }
    return docSnap.data() as EstablishmentType;
  }

  createEstablishmentType(establishmentType: EstablishmentType) {
    const collectionRef = collection(this.db, 'establishment-types');
    return addDoc(collectionRef, establishmentType);
  }

  updateEstablishmentType(establishmentType: EstablishmentType) {
    const docRef = doc(this.db, `establishment-types/${establishmentType.key}`);
    return setDoc(docRef, establishmentType);
  }

  getHotelByUser(userUid: string) {
    const url = new URL(`/hotel/user/${userUid}`, this.apiUrl);
    return this.http.get<HotelModel>(url.toString());
  }

  getHotel(hotelUid: string) {
    const url = new URL(`/hotel/${hotelUid}`, this.apiUrl);
    return this.http.get<HotelModel>(url.toString());
  }

  resetAdminPassword(email: string, password: string) {
    const url = new URL('/hotel/reset-admin-password', this.apiUrl);
    return this.http.post(url.toString(), { email, password });
  }

  getAllHotels() {
    const url = new URL(`/hotel`, this.apiUrl);
    return this.http.get<HotelModel[]>(url.toString());
  }

  getHotelsPaginated(params: { page: number; limit: number; name?: string; city?: string; country?: string }) {
    const url = new URL(`/hotel/paginated`, this.apiUrl);
    url.searchParams.set('page', String(params.page));
    url.searchParams.set('limit', String(params.limit));
    if (params.name) {
      url.searchParams.set('name', params.name);
    }
    if (params.city) {
      url.searchParams.set('city', params.city);
    }
    if (params.country) {
      url.searchParams.set('country', params.country);
    }
    return this.http.get<PaginatedHotels>(url.toString());
  }

  getHotelsList() {
    const url = new URL('/hotel/list', this.apiUrl);
    return this.http.get<HotelListItem[]>(url.toString());
  }

  getHotelZones(hotelUid: string) {
    const url = new URL(`/hotel/${hotelUid}/zone`, this.apiUrl);

    return this.http.get<ZoneModel[]>(url.toString());
  }

  getRooms(uidHotel: string) {
    const url = new URL(`/hotel/${uidHotel}/room`, this.apiUrl);
    return this.http.get<RoomModel[]>(url.toString());
  }

  getConfigHotel(hotelUid: string): Observable<HotelConfigModel | undefined> {
    const docRef = doc(this.db, `hotelConfig/${hotelUid}`) as DocumentReference<HotelConfigModel>;
    return docData<HotelConfigModel>(docRef);
  }

  getLinkWithPreview(previewUrl: string): Observable<LinkWithPreviewResponse> {
    const url = new URL('/link-preview?', this.apiUrl);
    return this.http.post<LinkWithPreviewResponse>(url.toString(), { url: previewUrl });
  }

  updateHotel(hotel: Partial<HotelModel>) {
    const url = new URL(`/hotel`, this.apiUrl);
    return this.http.post(url.toString(), {
      ...hotel,
      specialZones: [],
    });
  }

  async getLowRatingEmail(hotelUid: string) {
    const reference = ref(this.rtdb, `zoneEmail/${hotelUid}/lowRating`);
    const snapshot = await get(reference);
    return snapshot.val() as ZoneEmail;
  }

  async saveLowRatingEmail(hotelUid: string, email: string, state: boolean) {
    const reference = ref(this.rtdb, `zoneEmail/${hotelUid}/lowRating`);
    await set(reference, { email, state });
  }

  async updateConfigHotel(hotelUid: string, config: Partial<HotelConfigModel>) {
    const docRef = doc(this.db, `hotelConfig/${hotelUid}`);
    await setDoc(docRef, config, { merge: true });
  }

  getEcommercesByHotel(hotelUid: string) {
    const url = new URL(`/ecommerce/hotel/${hotelUid}`, this.apiUrl);
    return this.http.get<EcommerceModel[]>(url.toString());
  }

  saveAdmin(newHotelRequest: NewHotelRequest) {
    const url = new URL('/employer/admin', this.apiUrl);
    return this.http.post<{ hotel: { uid: string } }>(url.toString(), {
      ...newHotelRequest,
      special: newHotelRequest.special.map((s) => ({
        zone: s,
        status: true,
      })),
    });
  }
}
